import type { ICreateOfferCategoryCmd } from "@/data/commands/catalog/CreateOfferCategoryCmd";
import { Factory } from "@/data/dao/Factory";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { OfferCategoryHighlightType } from "@/data/model/Enums";
import type { IFullOfferCategory, IOfferCategory } from "@/data/model/OfferCategory";

import { ImageHelper } from "@/shared/helpers/ImageHelper";
import { translate } from "@/shared/localization/language";

import { MultiEntityInput } from "@/components/formControls/MultiEntityInput";

import { i18n } from "@/portal/Trans";
import { ImagePicker } from "@/portal/formControls/ImagePicker";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { BeforeSaveValidation } from "@/portal/helpers/BeforeSaveValidation";
import { UiNotification } from "@/portal/ui/Notification";

export class OfferCategoryForm extends BaseEntityForm<IFullOfferCategory, ICreateOfferCategoryCmd> {
    private availabilitySwitch: Switch;
    private nameField: MultiLanguageInput;
    private descriptionField: MultiLanguageInput;
    private offerInput: MultiEntityInput;
    private imagePicker: ImagePicker<IOfferCategory>;
    private squareImagePicker: ImagePicker<IOfferCategory>;
    private highlightSwitch: Switch;
    private highlightTypeSelect: Select<{ type: OfferCategoryHighlightType }>;
    private $highlightTypeRow: JQuery;
    private $internalNoteField: JQuery;
    private currentModel: IOfferCategory | undefined;
    private carriedOverImages: Promise<unknown> | undefined;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).offerCategoryDataSource);

        this.availabilitySwitch = new Switch(this.$form.find('input[name="isavailable"]'));

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        this.offerInput = MultiEntityInput.createFromInput(this.$form.find('.form-control[name="offers"]'), {
            dataSource: Factory.getInstance<CatalogDao>(CatalogDao).offerSearch,
        });

        this.descriptionField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="description"]'));
        this.imagePicker = new ImagePicker(
            Factory.getInstance<CatalogDao>(CatalogDao).offerCategoryImageDataSource
        ).renderTo(this.$form.find("#categoryimage"));

        this.squareImagePicker = new ImagePicker(
            Factory.getInstance<CatalogDao>(CatalogDao).offerCategorySquareImageDataSource
        ).renderTo(this.$form.find("#categorysquareimage"));

        this.$highlightTypeRow = this.$form.find(".highlight-type-row");

        this.highlightSwitch = new Switch(this.$form.find('input[name="highlight"]'));
        this.highlightSwitch.onChange = () => this.updateHighlightVisibility();

        this.highlightTypeSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="highlighttype"]'),
            this.highlightTypeDataSource,
            (c) => i18n("enums.OfferCategoryHighlightType." + OfferCategoryHighlightType[c.type]),
            false,
            undefined,
            "type"
        );
        this.highlightTypeSelect.onChange = () => this.updateHighlightVisibility();

        this.$internalNoteField = this.$form.find('.form-control[name="internalnote"]');

        this.updateHighlightVisibility();
    }

    public setFormData(model?: IFullOfferCategory) {
        this.currentModel = model;

        this.updateName(model === undefined ? "" : translate(model.name));
        this.availabilitySwitch.setValue(model === undefined ? false : model.isAvailable);
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.descriptionField.setValues(model === undefined ? undefined : model.description);
        this.offerInput.setValue(
            model === undefined
                ? undefined
                : model.offers.map((c) => {
                      return { id: c.id, name: c.name };
                  })
        );
        this.$internalNoteField.val(model === undefined || model.internalNote === undefined ? "" : model.internalNote);

        const imageModel = this.copyObjectId == null ? model : undefined;
        this.imagePicker.setValue(imageModel);
        this.squareImagePicker.setValue(imageModel);

        // a clone is created empty on the server, so the images of the source have to be
        // uploaded to it like any other pick - see onPostSave
        if (model !== undefined && this.copyObjectId !== undefined && this.carriedOverImages === undefined) {
            // saving before they are downloaded would fail the highlight validation below
            this.setLoading(true);
            this.carriedOverImages = Promise.all([
                ImageHelper.carryOverImage(this.imagePicker, model.originalImagePath),
                ImageHelper.carryOverImage(this.squareImagePicker, model.originalSquareImagePath),
            ]).then((carriedOver) => {
                this.setLoading(false);

                if (carriedOver.includes(false)) UiNotification.showWarning(i18n("offerCategory.imagesNotCopied"));
            });
        }

        this.highlightSwitch.setValue(model?.highlight ?? false);
        this.highlightTypeSelect.setValue(model?.highlightType ?? OfferCategoryHighlightType.Normal);
        this.updateHighlightVisibility();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateOfferCategoryCmd {
        const data: ICreateOfferCategoryCmd = {
            isAvailable: this.availabilitySwitch.getValue(),
            name: this.nameField.getValues(),
            description: this.descriptionField.getValues(),
            internalNote: this.$internalNoteField.val().length === 0 ? undefined : this.$internalNoteField.val(),
            offerIds: this.offerInput.getValue(),
            highlight: this.highlightSwitch.getValue(),
            highlightType: this.highlightSwitch.getValue()
                ? Number(this.highlightTypeSelect.getValue() ?? OfferCategoryHighlightType.Normal)
                : undefined,
        };

        let hasError = false;

        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        } else {
            if (
                (data.name.de !== undefined && data.name.de.length > 40) ||
                (data.name.fr !== undefined && data.name.fr.length > 40) ||
                (data.name.it !== undefined && data.name.it.length > 40) ||
                (data.name.en !== undefined && data.name.en.length > 40)
            ) {
                hasError = true;
                this.nameField.setError(true);
            } else {
                this.nameField.setError(false);
            }
        }

        if (data.description !== undefined) {
            if (
                (data.description.de !== undefined && data.description.de.length > 128) ||
                (data.description.fr !== undefined && data.description.fr.length > 128) ||
                (data.description.it !== undefined && data.description.it.length > 128) ||
                (data.description.en !== undefined && data.description.en.length > 128)
            ) {
                hasError = true;
                this.descriptionField.setError(true);
            } else {
                this.descriptionField.setError(false);
            }
        }

        this.squareImagePicker.setHasError(false);
        this.imagePicker.setHasError(false);

        if (data.highlight) {
            if (
                data.highlightType === OfferCategoryHighlightType.Normal &&
                !this.willHaveImage(this.squareImagePicker, this.currentModel?.squareImagePath)
            ) {
                this.squareImagePicker.setHasError(true);
                hasError = true;
            }

            if (
                data.highlightType === OfferCategoryHighlightType.FullWidth &&
                !this.willHaveImage(this.imagePicker, this.currentModel?.imagePath)
            ) {
                this.imagePicker.setHasError(true);
                hasError = true;
            }
        }

        return (hasError ? undefined : data) as any;
    }

    public onPostSave(category: IOfferCategory): Promise<IOfferCategory> {
        return Promise.resolve(this.carriedOverImages)
            .then(() => this.imagePicker.handle(category))
            .then(() => this.squareImagePicker.handle(category));
    }

    protected confirmSave(data: ICreateOfferCategoryCmd): Promise<boolean> {
        return BeforeSaveValidation.confirm(
            Factory.getInstance<CatalogDao>(CatalogDao).validateOfferCategoryBeforeSave({
                categoryId: this.id,
                name: data.name,
                description: data.description,
            }),
            this.$form.find('.form-control[name="name"]')
        );
    }

    private willHaveImage(picker: ImagePicker<IOfferCategory>, currentPath?: string): boolean {
        if (picker.pendingFile != null) return true;
        if (picker.hasPendingDelete) return false;

        // a clone is created without images, so the path of the source is not one of this category -
        // only the carried over upload counts, and there is none when its download failed
        return this.copyObjectId === undefined && currentPath != null;
    }

    private updateHighlightVisibility() {
        const isHighlighted = this.highlightSwitch.getValue();

        this.$highlightTypeRow.toggle(isHighlighted);
    }

    private highlightTypeDataSource: IDataSource<{ type: OfferCategoryHighlightType }> = {
        getData: () => {
            const items = [{ type: OfferCategoryHighlightType.Normal }, { type: OfferCategoryHighlightType.FullWidth }];

            return Promise.resolve({ items, offset: 0, limit: items.length, hasNext: false, hasPrev: false });
        },
    };
}
